import { useMemo } from 'react'

import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { AxiosRequestConfig } from 'axios'
import get from 'lodash/get'

import { getInvitations } from 'api/Cerebro/unscoped/organization'
import { useCerebroApiRequest } from 'hooks'
import { OrganizationInvitation } from 'types'

interface UseSignUpInvitationQueryOptions {
    enabled?: boolean
}

/**
 * Pre-auth lookup of a signing-up user's pending organization invitations.
 * Unscoped (no org headers) — the user has no org context yet.
 */
export function useSignUpInvitationQuery(
    email: string | undefined,
    options?: UseSignUpInvitationQueryOptions
): UseQueryResult<OrganizationInvitation[], Error> & { queryKey: unknown[] } {
    const makeCerebroApiRequest = useCerebroApiRequest({ throwOnError: true })

    const queryKey = useMemo(
        () => ['signUpInvitations', email ?? null],
        [email]
    )

    const result = useQuery({
        queryKey,
        queryFn: async (): Promise<OrganizationInvitation[]> => {
            const response = await makeCerebroApiRequest({
                request: getInvitations({
                    state: 'pending',
                    user_email: email,
                } as AxiosRequestConfig),
                suppressErrorAlerts: true,
            })
            return get(response, ['data', 'results'], [])
        },
        enabled: !!email && options?.enabled !== false,
    })

    return { ...result, queryKey }
}
